import React from 'react';
import DSBox from '../../../../design-system/DSBox';
import { Autocomplete } from '@mui/material';
import DSTextField from '../../../../design-system/DSTextField';
import DSCircularProgress from '../../../../design-system/DSCircularProgress';
import { useTheme } from '@mui/material';
import { LocalOfferOutlined } from '@mui/icons-material';
import { useLabelsSearch } from '../../hooks/useLabelsSearch';
import DSLabelChip from '../../../../design-system/DSLabelChip';
import useAppContext from '../../../../hooks/useAppContext';
import { AlertType } from '../../../../design-system/DSAlert';
import { MAX_LABELS_REACHED_ALERT } from '../../../../consts/alerts';

type LabelsInputProps = {
  selectedLabels: string[];
  onAddLabel: (label: string) => void;
  onRemoveLabel: (index: number) => void;
  error?: boolean;
  helperText?: string;
  minLabels?: number;
  maxLabels?: number;
  maxLabelsReachedMessage?: string;
};

const LabelsInput = ({
  selectedLabels,
  onAddLabel,
  onRemoveLabel,
  error = false,
  helperText,
  minLabels = 2,
  maxLabels = 3,
  maxLabelsReachedMessage = MAX_LABELS_REACHED_ALERT,
}: LabelsInputProps) => {
  const { spacing, palette } = useTheme();
  const { labels, loading, updateSearchQuery } = useLabelsSearch();
  const { showAlert } = useAppContext();

  const handleLabelsChange = (
    _event: React.SyntheticEvent,
    value: string[],
  ) => {
    if (value.length > maxLabels) {
      showAlert({
        alertType: AlertType.WARNING,
        message: maxLabelsReachedMessage,
      });
      return;
    }

    if (value.length > selectedLabels.length && value.length <= maxLabels) {
      const newLabel = value.find((label) => !selectedLabels.includes(label));
      if (newLabel) {
        onAddLabel(newLabel);
      }
    } else if (value.length < selectedLabels.length) {
      const removedLabel = selectedLabels.find(
        (label) => !value.includes(label),
      );
      if (removedLabel) {
        const index = selectedLabels.indexOf(removedLabel);
        onRemoveLabel(index);
      }
    }
  };

  const isMaxReached = selectedLabels.length >= maxLabels;

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: spacing(2),
        alignItems: 'flex-start',
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(0.5),
          flex: 1,
        }}
      >
        <Autocomplete
          multiple
          value={selectedLabels}
          options={labels}
          getOptionLabel={(option: string) => option}
          loading={loading}
          onInputChange={(_event, value) => updateSearchQuery(value)}
          onChange={handleLabelsChange}
          noOptionsText="No tags found"
          loadingText="Searching tags..."
          freeSolo={false}
          disableClearable
          popupIcon={null}
          renderTags={(value: string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <DSLabelChip
                label={option}
                {...getTagProps({ index })}
                key={option}
              />
            ))
          }
          renderInput={(params) => (
            <DSTextField
              {...params}
              placeholder={
                selectedLabels.length === 0
                  ? 'Search and add tags...'
                  : isMaxReached
                    ? ''
                    : 'Add more tags...'
              }
              size="small"
              error={error}
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <LocalOfferOutlined
                        sx={{
                          color: error
                            ? palette.error.main
                            : palette.text.secondary,
                          fontSize: '20px',
                          marginLeft: spacing(1),
                        }}
                      />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {loading ? (
                        <DSCircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          sx={{
            width: '100%',
            '& .MuiAutocomplete-inputRoot': {
              flexWrap: 'nowrap',
              gap: spacing(1),
              padding: '8px 16px',
              height: '40px',
            },
            '& .MuiAutocomplete-endAdornment': {
              display: 'none',
            },
          }}
        />
        {helperText && (
          <DSBox
            sx={{
              fontSize: '12px',
              color: error ? palette.error.main : palette.text.secondary,
              marginLeft: spacing(1.75),
            }}
          >
            {helperText}
          </DSBox>
        )}
        {!helperText && (
          <DSBox
            sx={{
              fontSize: '12px',
              color: palette.text.secondary,
              marginLeft: spacing(1.75),
            }}
          >
            {selectedLabels.length}/{maxLabels} tags selected (minimum{' '}
            {minLabels} required)
          </DSBox>
        )}
      </DSBox>
    </DSBox>
  );
};

export default LabelsInput;
